import React, { useRef, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import ReactDOM from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@commenda/utils';

interface ModalProps {
  children: React.ReactNode;
  isShown: boolean;
  hide: () => void;
  headerText?: string;
  hideCross?: boolean;
  additionalClassName?: string;
}

const Modal = ({
  isShown,
  hide,
  children,
  hideCross,
  headerText,
  additionalClassName,
}: ModalProps) => {
  const initialFocusRef = useRef(null);

  const modal = (
    <Transition.Root show={isShown} as={Fragment}>
      <Dialog
        initialFocus={initialFocusRef}
        as="div"
        className="relative z-30"
        onClose={hide}
        open={isShown}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-30 overflow-y-auto">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel
              ref={initialFocusRef}
              className={cn(
                `w-[328px] md:min-w-[440px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white text-left shadow-xl transition-all p-3 max-h-[75vh] overflow-y-scroll`,
                additionalClassName
              )}
            >
              <div className="flex flex-col mb-2">
                <div className="flex justify-between items-center">
                  {headerText && (
                    <h3 className="text-md text-center font-medium leading-6 text-gray-900">
                      {headerText}
                    </h3>
                  )}

                  {!hideCross && (
                    <button
                      type="button"
                      className="bg-transparent hover:bg-gray-100 active:bg-gray-300 p-1 transition-colors rounded-md group absolute right-2 top-2"
                      onClick={hide}
                    >
                      <XMarkIcon className="h-6 w-6 text-gray-400 group-hover:text-gray-600 group-active:text-gray-900" />
                    </button>
                  )}
                </div>
              </div>
              <div>{children}</div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );

  return isShown ? ReactDOM.createPortal(modal, document.body) : null;
};

export default Modal;
